import { useEffect, useRef, type RefObject } from 'react';

type Options = {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  menuRef: RefObject<HTMLElement | null>;
};

const TOUCH_INTERACTION_CLEAR_MS = 300;

const isInsideMenuOrTrigger = (
  target: Node | null,
  triggerRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
): boolean => {
  if (!target) {
    return false;
  }

  return Boolean(triggerRef.current?.contains(target) || menuRef.current?.contains(target));
};

const isTouchPointerEvent = (event: Event): boolean => {
  if (event.type === 'touchstart') {
    return true;
  }

  if (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent) {
    return event.pointerType === 'touch';
  }

  return false;
};

/**
 * Owns every way the menu can be asked to close: focus moving outside it,
 * a click outside it, or Escape/Tab being pressed while it's open.
 */
export const useMenuCloseState = ({ isOpen, onClose, triggerRef, menuRef }: Options): void => {
  const isPointerInteractionRef = useRef(false);
  const isTouchInteractionRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let clearPointerInteractionTimer: ReturnType<typeof setTimeout> | undefined;

    const markPointerInteraction = (event: Event) => {
      if (!isInsideMenuOrTrigger(event.target as Node, triggerRef, menuRef)) {
        return;
      }

      isPointerInteractionRef.current = true;

      if (isTouchPointerEvent(event)) {
        isTouchInteractionRef.current = true;
      }
    };

    const scheduleClearPointerInteraction = () => {
      if (clearPointerInteractionTimer) {
        clearTimeout(clearPointerInteractionTimer);
      }

      const clearDelay = isTouchInteractionRef.current ? TOUCH_INTERACTION_CLEAR_MS : 0;

      clearPointerInteractionTimer = setTimeout(() => {
        isPointerInteractionRef.current = false;
        isTouchInteractionRef.current = false;
      }, clearDelay);
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (isPointerInteractionRef.current) {
        return;
      }

      const target = event.target as Node;

      if (isInsideMenuOrTrigger(target, triggerRef, menuRef)) {
        return;
      }

      setTimeout(() => {
        if (
          isPointerInteractionRef.current ||
          isInsideMenuOrTrigger(document.activeElement, triggerRef, menuRef)
        ) {
          return;
        }

        onClose();
      }, 0);
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (isInsideMenuOrTrigger(event.target as Node, triggerRef, menuRef)) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        triggerRef.current?.focus();
        return;
      }

      if (event.key === 'Tab') {
        onClose();
      }
    };

    document.addEventListener('pointerdown', markPointerInteraction, true);
    document.addEventListener('mousedown', markPointerInteraction, true);
    document.addEventListener('touchstart', markPointerInteraction, true);
    document.addEventListener('pointerup', scheduleClearPointerInteraction, true);
    document.addEventListener('mouseup', scheduleClearPointerInteraction, true);
    document.addEventListener('touchend', scheduleClearPointerInteraction, true);
    document.addEventListener('pointercancel', scheduleClearPointerInteraction, true);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (clearPointerInteractionTimer) {
        clearTimeout(clearPointerInteractionTimer);
      }

      document.removeEventListener('pointerdown', markPointerInteraction, true);
      document.removeEventListener('mousedown', markPointerInteraction, true);
      document.removeEventListener('touchstart', markPointerInteraction, true);
      document.removeEventListener('pointerup', scheduleClearPointerInteraction, true);
      document.removeEventListener('mouseup', scheduleClearPointerInteraction, true);
      document.removeEventListener('touchend', scheduleClearPointerInteraction, true);
      document.removeEventListener('pointercancel', scheduleClearPointerInteraction, true);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, menuRef, onClose, triggerRef]);
};
